import { useEffect, useRef, useState } from 'react';

/**
 * Debounced loading-indicator visibility. `active` must stay true for
 * `delayMs` before this returns true — anything that resolves faster never
 * flashes an indicator at all (an "instaload" should look instant, not
 * skeleton-then-content). Once shown, it stays visible for at least
 * `minVisibleMs`, so an indicator that only just appeared doesn't flicker
 * straight back out when the load lands moments later.
 */
export default function useDelayedVisible(active, delayMs = 400, minVisibleMs = 400) {
  const [visible, setVisible] = useState(false);
  const shownAtRef = useRef(0);

  useEffect(() => {
    let timer;
    if (active) {
      if (!visible) {
        timer = setTimeout(() => {
          shownAtRef.current = Date.now();
          setVisible(true);
        }, delayMs);
      }
    } else if (visible) {
      const remaining = minVisibleMs - (Date.now() - shownAtRef.current);
      if (remaining > 0) {
        timer = setTimeout(() => setVisible(false), remaining);
      } else {
        setVisible(false);
      }
    }
    return () => clearTimeout(timer);
  }, [active, visible, delayMs, minVisibleMs]);

  return visible;
}
